import { z } from 'zod';
import { CreditLimitKindSchema } from '../config/credit-limits.ts';

const DaySchema = z.iso.date();

export const ConsoleUsageQuerySchema = z.object({ from: DaySchema, to: DaySchema });
export type ConsoleUsageQuery = z.infer<typeof ConsoleUsageQuerySchema>;

export const ConsoleUsageTotalsSchema = z.object({
  runs: z.number().int(),
  input_tokens: z.number().int(),
  output_tokens: z.number().int(),
  cache_read_tokens: z.number().int(),
  cache_write_tokens: z.number().int(),
  sandbox_ms: z.number().int(),
  credit_micros: z.number().int(),
  unpriced_runs: z.number().int(),
});
export type ConsoleUsageTotals = z.infer<typeof ConsoleUsageTotalsSchema>;

export const ConsoleUsageGroupSchema = ConsoleUsageTotalsSchema.extend({
  key: z.string().nullable(),
});
export type ConsoleUsageGroup = z.infer<typeof ConsoleUsageGroupSchema>;

export const ConsoleUsageSchema = z.object({
  from: DaySchema,
  to: DaySchema,
  totals: ConsoleUsageTotalsSchema,
  by_day: z.array(ConsoleUsageTotalsSchema.extend({ day: DaySchema })),
  by_organization: z.array(ConsoleUsageGroupSchema),
  by_plan: z.array(ConsoleUsageGroupSchema),
  by_purpose: z.array(ConsoleUsageGroupSchema),
});
export type ConsoleUsage = z.infer<typeof ConsoleUsageSchema>;

export const ConsoleCreditLimitSchema = z.object({
  kind: CreditLimitKindSchema,
  subject: z.string(),
  monthly_credit_micros: z.number().int(),
  updated_at: z.string(),
});
export type ConsoleCreditLimit = z.infer<typeof ConsoleCreditLimitSchema>;

export const ConsoleClientCreditsSchema = z.object({
  organization: z.string().nullable(),
  plan: z.string().nullable(),
  runs: z.number().int(),
  credit_micros: z.number().int(),
  limit: ConsoleCreditLimitSchema.nullable(),
});
export type ConsoleClientCredits = z.infer<typeof ConsoleClientCreditsSchema>;

export const ConsoleCreditLimitsSchema = z.object({
  month_start: z.string(),
  limits: z.array(ConsoleCreditLimitSchema),
  clients: z.array(ConsoleClientCreditsSchema),
});
export type ConsoleCreditLimits = z.infer<typeof ConsoleCreditLimitsSchema>;

export const ConsoleCreditLimitSetRequestSchema = z.object({
  kind: CreditLimitKindSchema,
  subject: z.string().trim().max(256),
  monthly_credit_micros: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
export type ConsoleCreditLimitSetRequest = z.infer<typeof ConsoleCreditLimitSetRequestSchema>;

export const ConsoleCreditLimitRemoveQuerySchema = z.object({
  kind: CreditLimitKindSchema,
  subject: z.string().max(256).default(''),
});
export type ConsoleCreditLimitRemoveQuery = z.infer<typeof ConsoleCreditLimitRemoveQuerySchema>;

export const ConsoleOverviewSchema = z.object({
  month_start: z.string(),
  credit_micros: z.number().int(),
  runs: z.number().int(),
  conversations: z.number().int(),
  conversations_rated_up: z.number().int(),
  conversations_rated_down: z.number().int(),
  conversations_with_errors: z.number().int(),
  tool_calls: z.number().int(),
  failed_tool_calls: z.number().int(),
  near_limit_ratio: z.number(),
  clients_near_limit: z.array(ConsoleClientCreditsSchema),
});
export type ConsoleOverview = z.infer<typeof ConsoleOverviewSchema>;
